import { useQuery } from '@apollo/client/react';
import { Duration, LocalDate } from '@js-joda/core';
import { Box, Typography, useTheme } from '@mui/material';
import * as React from 'react';
import { useRecordContext } from 'react-admin';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AdminGetUserDailyStepsDocument,
  AdminUserDetailsFragment,
} from '../../../../graphql/types';
import { monthDayYear } from '../../../../utils/date';

export const STEPS_CHART_DAYS = 30;
const CHART_HEIGHT_PX = 240;
const CHART_MAX_WIDTH_PX = 700;

export const MemberStepsChart: React.FC = () => {
  const record = useRecordContext<AdminUserDetailsFragment>();
  const theme = useTheme();

  const endDate = LocalDate.now();
  const startDate = endDate.minusDays(STEPS_CHART_DAYS);
  const stepDays = Duration.between(
    startDate.atStartOfDay(),
    endDate.atStartOfDay(),
  ).toDays();

  const { data, loading, error } = useQuery(AdminGetUserDailyStepsDocument, {
    variables: {
      userId: record?.id ?? '',
      dateRange: { start: startDate.toString(), end: endDate.toString() },
    },
    skip: !record?.id,
  });

  if (!record) {
    return null;
  }

  if (loading) {
    return <Typography>Loading steps data…</Typography>;
  }

  if (error) {
    return (
      <Typography color="error">
        Failed to load steps data: {error.message}
      </Typography>
    );
  }

  const dailySteps = data?.user?.dailySteps ?? [];

  if (dailySteps.length === 0) {
    return (
      <Typography>
        No steps data synced in the last {STEPS_CHART_DAYS} days
      </Typography>
    );
  }

  const stepsByDate = new Map(
    dailySteps.map(({ date, steps }) => [date, steps]),
  );
  const days = Array.from({ length: stepDays }, (_, i) => {
    const date = startDate.plusDays(i);
    return {
      date: monthDayYear(date),
      steps: stepsByDate.get(date.toString()) ?? 0,
    };
  });

  const daysWithData = days.filter(({ steps }) => steps > 0);
  const averageSteps = Math.round(
    daysWithData.reduce((total, { steps }) => total + steps, 0) /
      Math.max(daysWithData.length, 1),
  );
  const totalSteps = days.reduce((total, { steps }) => total + steps, 0);

  return (
    <Box>
      <Typography>
        Average {averageSteps.toLocaleString()} steps/day across{' '}
        {daysWithData.length} day
        {daysWithData.length === 1 ? '' : 's'} with data (
        {totalSteps.toLocaleString()} total)
      </Typography>
      <Box
        sx={{ height: CHART_HEIGHT_PX, maxWidth: CHART_MAX_WIDTH_PX, mt: 1 }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={days}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" minTickGap={32} tick={{ fontSize: 12 }} />
            <YAxis
              tickFormatter={(steps: number) => steps.toLocaleString()}
              tick={{ fontSize: 12 }}
              width={56}
            />
            <Tooltip
              formatter={(steps) => [`${Number(steps).toLocaleString()} steps`]}
            />
            <Bar dataKey="steps" fill={theme.palette.primary.main} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};
